import React from 'react'
import PropTypes from 'prop-types'
import styled, { withTheme } from 'styled-components'
import CheckboxCategoryGroup from '../CheckboxCategoryGroup'
import ChipCategoryGroup from '../ChipCategoryGroup'
import FreeTextInputGroup from '../FreeTextInputGroup'
import SliderCategoryGroup from '../SliderCategoryGroup'
import SliderCategoryGroupStateless from '../SliderCategoryGroupStateless'
import DropdownCategoryGroup from '../DropdownCategoryGroup'
import RadioButtonGroup from '../RadioButtonGroup'
import FilterSummaryCategoryGroup from '../FilterSummaryCategoryGroup'
import ButtonCategoryGroup from '../ButtonCategoryGroup'
import TileCategoryGroup from '../TileCategoryGroup'
import Expander from '../../atoms/Expander'
import { Box, Text, Divider, getPaletteColor } from 'pcln-design-system'
import { includeSliderContentPropType, markedValuesPropType } from '../../../helpers/commonPropTypes'
import { getParameterizedString } from '../../../helpers/parameterizedString'

const FilterItemsContainer = styled.div`
  margin-bottom: ${(props) => (props.needsExpander ? '8px' : '0')};
`

const FeaturedFilterHeader = styled(Text)`
  ${({ showChip }) =>
    showChip
      ? `text-align: center;
        margin-bottom: 0px;`
      : `text-align: left;
        margin-bottom: 8px;`}
`

const StyledResetButton = styled(Text.span)`
  color: ${getPaletteColor('primary.base')};
  cursor: pointer;
  flex: 1 1 auto;
  margin-left: auto;
`

const StyledHeader = styled(Text)`
  display: flex;
`

class FilterCategoryGroup extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isExpanded: props.isExpanded,
    }
    this.toggleExpander = this.toggleExpander.bind(this)
  }

  toggleExpander() {
    this.setState({
      isExpanded: !this.state.isExpanded,
    })
  }

  // this is for stateful slider to be reset
  static hardReset(filtersToShow, type) {
    if (type === 'slider') {
      return filtersToShow[0].resetKey
    }
    return undefined
  }

  renderHeader() {
    const { handleReset, filterName, largeCategoryHeader } = this.props

    if (filterName) {
      if (largeCategoryHeader) {
        return (
          <React.Fragment>
            <StyledHeader as='h2' fontSize={2} mb={1} mt={0} fontWeight='bold'>
              {filterName}
              {handleReset && (
                <StyledResetButton align='right' onClick={handleReset} fontSize={14}>
                  Reset
                </StyledResetButton>
              )}
            </StyledHeader>
            <Divider data-testid='divider' />
          </React.Fragment>
        )
      } else {
        return (
          <StyledHeader as='h2' fontSize={1} fontWeight='regular' mb={1} mt={0}>
            {filterName}
            {handleReset && (
              <StyledResetButton align='right' onClick={handleReset}>
                Reset
              </StyledResetButton>
            )}
          </StyledHeader>
        )
      }
    }
    return null
  }

  render() {
    /*
     Dynamic component typing!
     */
    const filterTypes = {
      button: ButtonCategoryGroup,
      checkbox: this.props.showChip ? ChipCategoryGroup : CheckboxCategoryGroup,
      dropdown: DropdownCategoryGroup,
      input: FreeTextInputGroup,
      radio: RadioButtonGroup,
      slider: this.props.stateless ? SliderCategoryGroupStateless : SliderCategoryGroup,
      summary: FilterSummaryCategoryGroup,
      tileInput: TileCategoryGroup,
    }

    const CategoryGroup = filterTypes[this.props.type]

    const lastAppliedFilterIndex = this.props.filters
      .map((item) => {
        return !item.applied ? 'nope' : 'applied'
      })
      .lastIndexOf('applied')

    const filterNamePrefix = this.props.filterName.split(' ').join('_')

    /*
      Options for Handling of applied filters
      1. Expand/collapse could be removed for a category group once a filter is applied in that group
      2. Default (collapsed) state length could be set to "whichever is greater" between "the passed
      config value" and "the index of the last applied filter in the group"
      3. Category groups could be sorted first by applied filters, and default state length could be
      set to "whichever is greater" between "the passed config value" and "the number applied filters
      in the group"
     */
    // option 1
    var itemsToShow = this.props.itemsToShow
    if (this.props.filters.length - itemsToShow === 1) {
      itemsToShow++
    }
    const unexpandedSize =
      itemsToShow && lastAppliedFilterIndex === -1 ? itemsToShow : this.props.filters.length

    // option 2
    // unexpandedSize = this.props.itemsToShow ? Math.max(this.props.itemsToShow, lastAppliedFilterIndex + 1) : this.props.filters.length

    // option 3 -- tbd
    //

    const needsExpander = unexpandedSize < this.props.filters.length
    const filtersToShow = this.props.filters.filter((item, index) => {
      return this.state.isExpanded || index < unexpandedSize
    })

    const textCollapsed = getParameterizedString(this.props?.textCollapsed, [
      this.props.filters.length - unexpandedSize,
    ])

    return (
      <Box pb={this.props.sectionPb} className={this.props.className}>
        {!this.props.hideFilterName && this.renderHeader()}
        <FilterItemsContainer needsExpander={needsExpander}>
          {this.props.featuredFilters && (
            <>
              <FeaturedFilterHeader fontSize={0} mt={3} showChip={this.props.showChip}>
                {this.props.featuredFilters.filterName}
              </FeaturedFilterHeader>
              <CategoryGroup
                activeSearch={this.props.activeSearch}
                filterName={this.props.featuredFilters.filterName}
                filterNamePrefix={filterNamePrefix}
                filters={this.props.featuredFilters.filters}
                handleAfterChange={this.props.handleAfterChange}
                handleChange={this.props.handleChange}
                handleSubmit={this.props.handleSubmit}
                hideCloseIcon={this.props.hideCloseIcon}
                key={FilterCategoryGroup.hardReset(this.props.featuredFilters.filters, this.props.type)}
                scrollMode={this.props.scrollMode}
                shouldPadConsistently={this.props.shouldPadConsistently}
                showChip={this.props.showChip}
                showIncrementalSlider={this.props.showIncrementalSlider}
                showSearchIconButton={this.props.showSearchIconButton}
                theme={this.props.theme}
                useInternalState={this?.props?.useInternalState}
                wrapLabel={this.props.wrapLabel}
              />
              <FeaturedFilterHeader fontSize={0} mt={3} showChip={this.props.showChip}>
                All {this.props.filterName}
              </FeaturedFilterHeader>
            </>
          )}
          <CategoryGroup
            activeSearch={this.props.activeSearch}
            avgRetailPrice={this.props.avgRetailPrice}
            buttonIsGrayWithNoInput={this.props.buttonIsGrayWithNoInput}
            featuredFilters={this.props.featuredFilters}
            filterName={this.props.filterName}
            filterNamePrefix={filterNamePrefix}
            filters={filtersToShow}
            filterSummaryTitle={this.props.filterSummaryTitle}
            handleAfterChange={this.props.handleAfterChange}
            handleChange={this.props.handleChange}
            handleSubmit={this.props.handleSubmit}
            hasParentCheckbox={this.props.hasParentCheckbox}
            hideCloseIcon={this.props.hideCloseIcon}
            hideLastPlus={this.props.hideLastPlus}
            includeDivider={this.props.includeDivider}
            includeSliderContent={this.props.includeSliderContent}
            includeSliderHeadline={this.props.includeSliderHeadline}
            isAccordion={this.props.isAccordion}
            isParentOptional={this.props.isParentOptional}
            key={FilterCategoryGroup.hardReset(filtersToShow, this.props.type)}
            markedValues={this.props.markedValues}
            priceGuidanceAllStarAvgPrice={this.props.priceGuidanceAllStarAvgPrice}
            priceGuidanceFilterData={this.props.priceGuidanceFilterData}
            priceGuidanceNewLabelStyle={this.props.priceGuidanceNewLabelStyle}
            scrollMode={this.props.scrollMode}
            searchFilterFontSize={this.props.searchFilterFontSize}
            shouldPadConsistently={this.props.shouldPadConsistently}
            showChip={this.props.showChip}
            showIncrementalSlider={this.props.showIncrementalSlider}
            showSearchIconButton={this.props.showSearchIconButton}
            showSliderInputs={this.props.showSliderInputs}
            sliderInputMaxLabel={this.props.sliderInputMaxLabel}
            sliderInputMinLabel={this.props.sliderInputMinLabel}
            theme={this.props.theme}
            useInternalState={this?.props?.useInternalState}
            wrapLabel={this.props.wrapLabel}
          />
        </FilterItemsContainer>
        {needsExpander && (
          <Expander
            filterCatagory={this.props.filterName}
            isExpanded={this.state.isExpanded}
            onClick={this.toggleExpander}
            showChip={this.props.showChip}
            textCollapsed={textCollapsed}
            textExpanded={this.props.textExpanded}
          />
        )}
      </Box>
    )
  }
}

const propTypes = {
  activeSearch: PropTypes.bool,
  avgRetailPrice: PropTypes.number,
  buttonIsGrayWithNoInput: PropTypes.bool,
  className: PropTypes.string, // generated by styled-components, don't pass explicitly
  featuredFilters: PropTypes.shape({
    filterName: PropTypes.string,
    filters: PropTypes.array,
  }),
  filterName: PropTypes.string, // becomes an h3
  filters: PropTypes.array.isRequired, // one child per filterable item to control show-hides
  filterSummaryTitle: PropTypes.string,
  handleAfterChange: PropTypes.func,
  handleChange: PropTypes.func.isRequired,
  handleReset: PropTypes.func,
  handleSubmit: PropTypes.func,
  hasParentCheckbox: PropTypes.bool,
  hideCloseIcon: PropTypes.bool,
  hideFilterName: PropTypes.bool,
  hideLastPlus: PropTypes.bool,
  includeDivider: PropTypes.bool,
  includeSliderContent: includeSliderContentPropType,
  includeSliderHeadline: PropTypes.string,
  isAccordion: PropTypes.bool,
  isExpanded: PropTypes.bool,
  isParentOptional: PropTypes.bool,
  itemsToShow: PropTypes.number, // number to show by default
  largeCategoryHeader: PropTypes.bool,
  markedValues: markedValuesPropType,
  priceGuidanceAllStarAvgPrice: PropTypes.number,
  priceGuidanceFilterData: PropTypes.array,
  priceGuidanceNewLabelStyle: PropTypes.bool,
  scrollMode: PropTypes.bool,
  searchFilterFontSize: PropTypes.string,
  sectionPb: PropTypes.number,
  shouldPadConsistently: PropTypes.bool,
  showChip: PropTypes.bool,
  showGuestRatingRadio: PropTypes.bool,
  showIncrementalSlider: PropTypes.bool,
  showSearchIconButton: PropTypes.bool,
  showSliderInputs: PropTypes.bool,
  sliderInputMaxLabel: PropTypes.string,
  sliderInputMinLabel: PropTypes.string,
  stateless: PropTypes.bool,
  textCollapsed: PropTypes.string, // "show more" text
  textExpanded: PropTypes.string, // "show fewer" text
  theme: PropTypes.shape({ colors: PropTypes.object.isRequired }).isRequired,
  type: PropTypes.string.isRequired,
  useInternalState: PropTypes.bool,
  wrapLabel: PropTypes.bool,
}

const defaultProps = {
  filterName: '',
  hasParentCheckbox: false,
  hideFilterName: false,
  includeDivider: false,
  isAccordion: false,
  isExpanded: false,
  isParentOptional: false,
  largeCategoryHeader: false,
  sectionPb: 4,
  shouldPadConsistently: false,
  textCollapsed: 'show more',
  textExpanded: 'show less',
  wrapLabel: false,
  hideLastPlus: false,
}

FilterCategoryGroup.propTypes = propTypes
FilterCategoryGroup.defaultProps = defaultProps

export default withTheme(FilterCategoryGroup)
